import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Camera, Wallet, Phone, MapPin, Mail, Upload, Loader2 } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  EditProfileDialog — Iter 45.14                                             */
/*                                                                             */
/*  Self-service form that PATCHes `/api/profile/me`. Never touches salary/    */
/*  role/RBAC (backend enforces this). Avatar is resized client-side to 256px  */
/*  JPEG q0.75 so the base64 payload stays under ~60 KB before hitting Mongo.  */
/* -------------------------------------------------------------------------- */

async function resizeImage(file, size = 256, quality = 0.75) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf]);
  const bitmap = await createImageBitmap(blob);
  const min = Math.min(bitmap.width, bitmap.height);
  // center-crop to a square then draw at target size
  const sx = (bitmap.width - min) / 2;
  const sy = (bitmap.height - min) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, sx, sy, min, min, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function EditProfileDialog({ open, onClose, me, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && me) {
      setDraft({
        avatar_url:         me.avatar_url         || "",
        bank_name:          me.bank_name          || "",
        ifsc:               me.ifsc               || "",
        account_number:     me.account_number     || "",
        upi_id:             me.upi_id             || "",
        personal_email:     me.personal_email     || "",
        contact_number:     me.contact_number     || "",
        alt_contact_number: me.alt_contact_number || "",
        emergency_contact:  me.emergency_contact  || "",
        current_address:    me.current_address    || "",
      });
    }
  }, [open, me]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please choose an image file (JPG/PNG/WebP).");
      e.target.value = "";
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (max 8 MB before resize).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await resizeImage(file);
      set("avatar_url", dataUrl);
    } catch (err) {
      toast.error("Could not read that image — try a different file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/profile/me", draft);
      toast.success("Profile updated");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const initials = (me?.name || "U").trim().split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-purple-600" /> Edit Your Details
          </DialogTitle>
          <DialogDescription>
            Update your profile picture, contact details, address and banking info. Bank details automatically appear on the Payroll sheet once saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {draft.avatar_url ? (
                <img
                  src={draft.avatar_url}
                  alt="Profile"
                  className="h-20 w-20 rounded-full object-cover border-2 border-purple-200 shadow-sm"
                  data-testid="edit-profile-avatar-preview"
                />
              ) : (
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 text-white flex items-center justify-center text-2xl font-bold shadow-sm">
                  {initials}
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">Profile picture</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Square works best · we shrink it to 256 px automatically.</div>
              <div className="mt-2 flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="edit-profile-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="edit-profile-pick-file">
                  <Upload className="h-4 w-4 mr-1" /> {draft.avatar_url ? "Change" : "Upload"}
                </Button>
                {draft.avatar_url && (
                  <Button size="sm" variant="outline" onClick={() => set("avatar_url", "")} className="text-slate-500 hover:text-red-600 hover:border-red-200" data-testid="edit-profile-remove-avatar">
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bank details */}
          <Section title="Bank Details" icon={Wallet}>
            <Grid>
              <Field label="Bank Name">
                <Input value={draft.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="HDFC Bank" data-testid="edit-bank-name" />
              </Field>
              <Field label="IFSC Code">
                <Input value={draft.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} placeholder="HDFC0001234" className="uppercase font-mono tracking-wider" data-testid="edit-ifsc" />
              </Field>
              <Field label="Account Number">
                <Input value={draft.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="1234 5678 9012" className="font-mono" data-testid="edit-account-number" />
              </Field>
              <Field label="UPI ID">
                <Input value={draft.upi_id} onChange={(e) => set("upi_id", e.target.value)} placeholder="yourname@okhdfc" data-testid="edit-upi-id" />
              </Field>
            </Grid>
          </Section>

          {/* Contact */}
          <Section title="Contact" icon={Phone}>
            <Grid>
              <Field label="Personal Email">
                <Input type="email" value={draft.personal_email} onChange={(e) => set("personal_email", e.target.value)} placeholder="you@gmail.com" data-testid="edit-personal-email" />
              </Field>
              <Field label="Contact Number">
                <Input value={draft.contact_number} onChange={(e) => set("contact_number", e.target.value)} placeholder="+91 98765 43210" data-testid="edit-contact-number" />
              </Field>
              <Field label="Alternate Contact Number">
                <Input value={draft.alt_contact_number} onChange={(e) => set("alt_contact_number", e.target.value)} placeholder="+91 98765 12345" data-testid="edit-alt-contact-number" />
              </Field>
              <Field label="Emergency Contact Number">
                <Input value={draft.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} placeholder="+91 98765 67890" data-testid="edit-emergency-contact" />
              </Field>
            </Grid>
          </Section>

          {/* Address */}
          <Section title="Current Address" icon={MapPin}>
            <Textarea
              rows={3}
              value={draft.current_address}
              onChange={(e) => set("current_address", e.target.value)}
              placeholder="Flat / Street, City, State, PIN"
              data-testid="edit-current-address"
            />
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-profile-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="edit-profile-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5 text-purple-500" /> {title}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
